import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
  Shield, DollarSign, BarChart3, Search, AlertCircle,
  CheckCircle, Loader, Info, AlertTriangle, Settings, X
} from 'lucide-react';
import './DiagnosticChecksManager.css';

// Icônes par famille
const FAMILY_ICONS = {
  hygiene: Shield,
  budget: DollarSign,
  performance: BarChart3,
  tracking: Search
};

// Labels des familles
const FAMILY_LABELS = {
  hygiene: 'Hygiène du compte',
  budget: 'Budget',
  performance: 'Performances',
  tracking: 'Tracking'
};

// Icônes de sévérité
const SEVERITY_ICONS = {
  CRITICAL: AlertCircle,
  WARNING: AlertTriangle,
  INFO: Info
};

const SEVERITY_COLORS = {
  CRITICAL: '#ef4444',
  WARNING: '#f59e0b',
  INFO: '#1a1a1a'
};

const SEVERITY_LABELS = {
  CRITICAL: 'Critique',
  WARNING: 'Avertissement',
  INFO: 'Information'
};

const DiagnosticChecksManager = ({ token, accountId, onClose }) => {
  const [checkTypes, setCheckTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(null);
  const [updatingFamily, setUpdatingFamily] = useState(null);
  // Ce que le serveur déclare réglable : libellé, bornes, aide. L'écran ne connaît donc aucun
  // nom de réglage en dur — un nouveau réglage apparaît du seul fait qu'il est déclaré côté API.
  const [reglagesAjustables, setReglagesAjustables] = useState({});
  // Le contrôle dont la modale de paramètres est ouverte, et la saisie en cours par réglage.
  const [checkEnParametres, setCheckEnParametres] = useState(null);
  const [brouillon, setBrouillon] = useState({});
  const [erreurModale, setErreurModale] = useState(null);
  const [enregistrement, setEnregistrement] = useState(false);

  // Charger les check types pour ce compte
  const fetchCheckTypes = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/diagnostic/check-types/${accountId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setCheckTypes(data.data);
        setReglagesAjustables(data.reglagesAjustables || {});
      } else {
        setError(data.error || 'Erreur lors du chargement');
      }
    } catch (err) {
      setError('Erreur de connexion');
      console.error('Erreur fetchCheckTypes:', err);
    } finally {
      setLoading(false);
    }
  }, [token, accountId]);

  useEffect(() => {
    fetchCheckTypes();
  }, [fetchCheckTypes]);

  // Les réglages ajustables d'un contrôle : ceux de ses `params` que le serveur déclare
  // modifiables. Trois contrôles sur 51 en portent, les autres ne rendent donc pas de bouton.
  const clesReglables = (check) =>
    Object.keys(reglagesAjustables).filter(cle => check?.params && cle in check.params);

  // Les autres contrôles qui subiront le même réglage, pour que la modale le dise avant
  // d'enregistrer plutôt que de le faire découvrir en ouvrant un contrôle voisin.
  const fratrieDuReglage = (check, cle) =>
    reglagesAjustables[cle]?.partage
      ? checkTypes.filter(ct => ct.id !== check.id && ct.params && cle in ct.params)
      : [];

  const ouvrirParametres = (check) => {
    const saisie = {};
    for (const cle of clesReglables(check)) saisie[cle] = String(check.params[cle]);
    setBrouillon(saisie);
    setErreurModale(null);
    setCheckEnParametres(check);
  };

  const fermerParametres = () => {
    setCheckEnParametres(null);
    setBrouillon({});
    setErreurModale(null);
  };

  // Enregistrer les réglages saisis. Une valeur égale au défaut du catalogue n'est pas recopiée :
  // le serveur retire la surcharge, et le réglage se remet à suivre les révisions du catalogue.
  const enregistrerParametres = async () => {
    const check = checkEnParametres;
    const aEcrire = {};

    for (const cle of clesReglables(check)) {
      const regle = reglagesAjustables[cle];
      const valeur = Number(brouillon[cle]);
      if (brouillon[cle] === '' || !Number.isFinite(valeur)
          || (regle.entier && !Number.isInteger(valeur))
          || valeur < regle.min || valeur > regle.max) {
        setErreurModale(`${regle.libelle} : un nombre entre ${regle.min} et ${regle.max} est attendu`);
        return;
      }
      if (valeur !== check.params[cle]) aEcrire[cle] = valeur;
    }

    if (Object.keys(aEcrire).length === 0) {
      fermerParametres();
      return;
    }

    setEnregistrement(true);
    setErreurModale(null);
    try {
      const response = await fetch(`/api/diagnostic/check-types/${accountId}/${check.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ params: aEcrire })
      });
      const data = await response.json();
      if (data.success) {
        // Le serveur rend tous les contrôles touchés, la fratrie comprise : sans ça, elle
        // afficherait l'ancienne valeur jusqu'au prochain chargement de l'écran.
        const parId = new Map((data.checksMisAJour || [data.data]).map(ct => [ct.id, ct]));
        setCheckTypes(prev => prev.map(ct => parId.has(ct.id)
          ? { ...ct, params: parId.get(ct.id).params, paramsModifies: parId.get(ct.id).paramsModifies }
          : ct));
        fermerParametres();
      } else {
        setErreurModale(data.error || 'Erreur lors de la mise à jour');
      }
    } catch (err) {
      setErreurModale('Erreur de connexion');
      console.error('Erreur enregistrerParametres:', err);
    } finally {
      setEnregistrement(false);
    }
  };

  // Toggle un check type pour ce compte
  const toggleCheckType = async (checkType) => {
    setUpdating(checkType.id);
    try {
      const response = await fetch(`/api/diagnostic/check-types/${accountId}/${checkType.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ isActive: !checkType.isActive })
      });
      const data = await response.json();
      if (data.success) {
        // Mettre à jour localement
        setCheckTypes(prev => prev.map(ct =>
          ct.id === checkType.id ? { ...ct, isActive: !ct.isActive } : ct
        ));
      } else {
        setError(data.error || 'Erreur lors de la mise à jour');
      }
    } catch (err) {
      setError('Erreur de connexion');
      console.error('Erreur toggleCheckType:', err);
    } finally {
      setUpdating(null);
    }
  };

  // Enregistrer un réglage pour ce compte. Une valeur vide, ou égale au défaut du catalogue,
  // revient au défaut : le serveur retire alors la surcharge plutôt que de la recopier.
  // Toggle tous les checks d'une famille
  const toggleFamilyChecks = async (family, checks, activate) => {
    setUpdatingFamily(family);
    setError(null);

    try {
      const checkTypeIds = checks.map(c => c.id);
      const response = await fetch(`/api/diagnostic/check-types/${accountId}/batch`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ checkTypeIds, isActive: activate })
      });

      const data = await response.json();

      if (data.success) {
        // Mettre à jour localement tous les checks de cette famille
        setCheckTypes(prev => prev.map(ct =>
          checkTypeIds.includes(ct.id) ? { ...ct, isActive: activate } : ct
        ));
      } else {
        setError(data.error || 'Erreur lors de la mise à jour');
      }
    } catch (err) {
      setError('Erreur de connexion');
      console.error('Erreur toggleFamilyChecks:', err);
    } finally {
      setUpdatingFamily(null);
    }
  };

  // Grouper par famille
  const checksByFamily = checkTypes.reduce((acc, check) => {
    const family = check.family || 'other';
    if (!acc[family]) acc[family] = [];
    acc[family].push(check);
    return acc;
  }, {});

  // Ordre des familles
  const familyOrder = ['hygiene', 'budget', 'performance', 'tracking'];

  if (loading) {
    return (
      <div className="checks-manager-loading">
        <Loader className="animate-spin" size={24} />
        <span>Chargement des diagnostics...</span>
      </div>
    );
  }

  return (
    <div className="checks-manager">
      <div className="checks-manager-header">
        <div className="checks-manager-title-row">
          <h2>Détail du diagnostic</h2>
          <span className="checks-manager-count">
            {checkTypes.filter(c => c.isActive).length}/{checkTypes.length} activés
          </span>
        </div>
        <p className="checks-manager-description">
          Activez ou désactivez les vérifications effectuées lors d'un scan.
          Les checks désactivés ne seront pas exécutés et n'affecteront pas le score.
        </p>
      </div>

      {error && (
        <div className="checks-manager-error">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      <div className="checks-manager-content">
        {familyOrder.map(family => {
          const checks = checksByFamily[family] || [];
          if (checks.length === 0) return null;

          const FamilyIcon = FAMILY_ICONS[family] || Shield;
          const activeCount = checks.filter(c => c.isActive).length;
          const hasActiveChecks = activeCount > 0;
          const isFamilyUpdating = updatingFamily === family;

          return (
            <div key={family} className="checks-family-section">
              <div className="checks-family-header">
                <div className="checks-family-toggle">
                  {isFamilyUpdating ? (
                    <div className="checks-family-updating">
                      <Loader className="animate-spin" size={16} />
                    </div>
                  ) : (
                    <label className="check-toggle">
                      <input
                        type="checkbox"
                        checked={hasActiveChecks}
                        onChange={() => toggleFamilyChecks(family, checks, !hasActiveChecks)}
                        disabled={isFamilyUpdating}
                      />
                      <span className="check-toggle-slider"></span>
                    </label>
                  )}
                </div>
                <div className="checks-family-title">
                  <FamilyIcon size={20} />
                  <span>{FAMILY_LABELS[family] || family}</span>
                </div>
                <div className="checks-family-count">
                  {activeCount}/{checks.length} actifs
                </div>
              </div>

              <div className="checks-list">
                {checks.map(check => {
                  const SeverityIcon = SEVERITY_ICONS[check.severity] || Info;
                  const isUpdating = updating === check.id;

                  return (
                    <div
                      key={check.id}
                      className={`check-item ${!check.isActive ? 'check-item-disabled' : ''}`}
                    >
                      <div className="check-item-toggle">
                        <label className="check-toggle">
                          <input
                            type="checkbox"
                            checked={check.isActive}
                            onChange={() => toggleCheckType(check)}
                            disabled={isUpdating}
                          />
                          <span className="check-toggle-slider"></span>
                        </label>
                      </div>

                      <div className="check-item-content">
                        <div className="check-item-header">
                          <span className="check-item-label">{check.label}</span>
                          <span
                            className="check-item-severity"
                            style={{ color: SEVERITY_COLORS[check.severity] }}
                            title={SEVERITY_LABELS[check.severity]}
                          >
                            <SeverityIcon size={14} />
                            {SEVERITY_LABELS[check.severity]}
                          </span>
                        </div>
                        {check.description && (
                          <p className="check-item-description">{check.description}</p>
                        )}
                        {check.recommendation && (
                          <p className="check-item-recommendation">
                            <CheckCircle size={12} />
                            {check.recommendation}
                          </p>
                        )}
                        {clesReglables(check).length > 0 && (
                          <button
                            type="button"
                            className="check-item-parametres"
                            onClick={() => ouvrirParametres(check)}
                          >
                            <Settings size={13} />
                            Paramètres
                            {(check.paramsModifies || []).length > 0 && (
                              <span className="check-item-parametres-pastille">personnalisés</span>
                            )}
                          </button>
                        )}
                      </div>

                      {isUpdating && (
                        <div className="check-item-updating">
                          <Loader className="animate-spin" size={16} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {checkEnParametres && ReactDOM.createPortal(
        <div
          className="check-parametres-fond"
          onClick={() => !enregistrement && fermerParametres()}
        >
          <div className="check-parametres-modale" onClick={(e) => e.stopPropagation()}>
            <div className="check-parametres-entete">
              <div>
                <h3>Paramètres du contrôle</h3>
                <p>{checkEnParametres.label}</p>
              </div>
              <button
                type="button"
                className="check-parametres-fermer"
                onClick={fermerParametres}
                disabled={enregistrement}
                aria-label="Fermer"
              >
                <X size={18} />
              </button>
            </div>

            <div className="check-parametres-corps">
              {clesReglables(checkEnParametres).map(cle => {
                const regle = reglagesAjustables[cle];
                const defaut = checkEnParametres.defaultParams?.[cle];
                const fratrie = fratrieDuReglage(checkEnParametres, cle);

                return (
                  <div className="check-parametre" key={cle}>
                    <label className="check-parametre-libelle" htmlFor={`parametre-${cle}`}>
                      {regle.libelle}
                    </label>
                    <div className="check-parametre-saisie">
                      <input
                        id={`parametre-${cle}`}
                        type="number"
                        inputMode="numeric"
                        min={regle.min}
                        max={regle.max}
                        step={regle.entier ? 1 : 'any'}
                        value={brouillon[cle] ?? ''}
                        disabled={enregistrement}
                        onChange={(e) => setBrouillon(prev => ({ ...prev, [cle]: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === 'Enter') enregistrerParametres(); }}
                      />
                      <span className="check-parametre-unite">{regle.unite}</span>
                      {String(defaut) !== String(brouillon[cle]) && (
                        <button
                          type="button"
                          className="check-parametre-defaut"
                          disabled={enregistrement}
                          onClick={() => setBrouillon(prev => ({ ...prev, [cle]: String(defaut) }))}
                        >
                          Revenir au défaut ({defaut})
                        </button>
                      )}
                    </div>
                    {regle.aide && <p className="check-parametre-aide">{regle.aide}</p>}
                    {fratrie.length > 0 && (
                      <p className="check-parametre-portee">
                        <Info size={13} />
                        <span>
                          Ce réglage décrit le compte, pas le contrôle : il s'appliquera aussi à{' '}
                          {fratrie.map(ct => ct.label).join(' et ')}.
                        </span>
                      </p>
                    )}
                  </div>
                );
              })}

              {erreurModale && (
                <div className="check-parametres-erreur">
                  <AlertCircle size={14} />
                  {erreurModale}
                </div>
              )}
            </div>

            <div className="check-parametres-pied">
              <button
                type="button"
                className="check-parametres-annuler"
                onClick={fermerParametres}
                disabled={enregistrement}
              >
                Annuler
              </button>
              <button
                type="button"
                className="check-parametres-valider"
                onClick={enregistrerParametres}
                disabled={enregistrement}
              >
                {enregistrement ? <Loader className="animate-spin" size={14} /> : null}
                Enregistrer
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default DiagnosticChecksManager;
